import { yupResolver } from '@hookform/resolvers/yup';
import { isValidPhoneNumber } from 'libphonenumber-js/min';
import { UserCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import photo from '../assets/images/profile-photo-base.png';
import {
  Button,
  Pressable,
  Checkbox,
  Input,
  Loader,
  TabList,
  PhoneInputWithMask,
  Tooltip,
  CustomSelect,
  Card,
  Heading,
  useFilePicker,
} from '../shared/ui';
import ConfirmCodex from '../components/profile/confirm-codex';
import { UnsavedChangesModal } from '../components/profile/unsaved-changes-modal';
import { LogoutButton } from '../components/profile/logout-button';
import { ArrowTopRight } from '../components/icons/arrow-top-right';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { useChangeUserMutation } from '../services/users.api';
import {
  useGetPsychologistProfileQuery,
  useUpdatePsychologistProfileMutation,
  useUploadPsychologistAvatarMutation,
  type PsychologistProfilePatch,
} from '../services/psychologistProfile.api';
import { getApiErrorCode, getApiErrorStatus } from '../utils/apiError';
import { selectUser, setUserData } from '../store/auth.slice';
import { HotOfferCard } from '../components/profile/hot-offer-card';
import { CatalogProfileTab } from '../components/profile/CatalogProfileTab';
import {
  getProfileHotOfferCompletedStorageKey,
  getProfileHotOfferSnoozedUntilStorageKey,
  parseProfileHotOfferPayload,
} from '../components/profile/hot-offer-data';
import { FEATURE_FLAG_ENABLE_PROFILE_CATALOG, FEATURE_FLAG_PROFILE_HOT_OFFER } from '../constants/featureFlags';
import { useGetSpecializationsQuery } from '../services/directus.api';

const schema = yup.object().shape({
  firstName: yup.string().required('Заполните поле'),
  lastName: yup.string().required('Заполните поле'),
  middleName: yup.string(),
  specialization: yup.string(),
  phone: yup
    .string()
    .required('Введите телефон')
    .test('is-valid-phone', 'Некорректный номер телефона', (value) => isValidPhoneNumber(value || '')),
  agreeToReceiveEmails: yup.boolean(),
});

type ProfileFormValues = yup.InferType<typeof schema>;

type Tab = 'general' | 'catalog';

const ALLOWED_AVATAR_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const MAX_AVATAR_SIZE_BYTES = 5 * 1024 * 1024;

const Profile = () => {
  const userData = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [isCatalogTabDirty, setIsCatalogTabDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<Tab | null>(null);
  const [isShowCodex, setIsShowCodex] = useState(false);
  const [dismissedHotOfferId, setDismissedHotOfferId] = useState<string | null>(null);
  const [changeUser] = useChangeUserMutation();
  const { data: profile } = useGetPsychologistProfileQuery();
  const [updateProfile] = useUpdatePsychologistProfileMutation();
  const [uploadAvatar, { isLoading: isLoadingAva }] = useUploadPsychologistAvatarMutation();
  const { data: specializationResponse } = useGetSpecializationsQuery();
  const hotOfferPayload = useFeatureFlagPayload(FEATURE_FLAG_PROFILE_HOT_OFFER);
  const isCatalogProfileEnabled = useFeatureFlagEnabled(FEATURE_FLAG_ENABLE_PROFILE_CATALOG);

  const { control, handleSubmit, reset, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      middleName: '',
      phone: '',
      specialization: '',
      agreeToReceiveEmails: false,
    },
  });

  useEffect(() => {
    if (!profile) return;
    reset({
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      middleName: profile.middle_name || '',
      specialization: profile.specialization || '',
      phone: profile.phone_number || '',
      // Согласие на рассылки живёт в legacy user и приходит из /users/me.
      agreeToReceiveEmails: Boolean(userData?.agree_to_receive_emails),
    });
  }, [profile, userData, reset]);

  useEffect(() => {
    if (userData && !userData.agree_with_code) {
      showCodex();
    }
  }, [userData]);

  const showCodex = () => {
    setIsShowCodex(true);
  };

  const onChangeAvatar = async ([file]: File[]) => {
    const extension = file.name.split('.').at(-1)?.toLowerCase();
    if (!extension || !ALLOWED_AVATAR_EXTENSIONS.includes(extension)) {
      toast.warn('Загрузите изображение в формате .jpg, .jpeg, .png или .webp');
      return;
    }
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      toast.warn('Размер изображения не должен превышать 5 МБ');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      await uploadAvatar(formData).unwrap();
      toast.success('Фото профиля обновлено');
    } catch (error: unknown) {
      const status = getApiErrorStatus(error);
      toast.error(
        status === 413
          ? 'Файл слишком большой (максимум 5 МБ)'
          : status === 400
            ? 'Некорректный файл изображения'
            : status === 502 || status === 503
              ? 'Сервис хранения файлов временно недоступен, попробуйте позже'
              : 'Не удалось загрузить фото',
      );
    }
  };
  const avatarPicker = useFilePicker({ accept: '.png,.jpg,.jpeg,.webp', onFiles: onChangeAvatar });

  const isNewsletterChanged = useCallback(
    (data: Partial<ProfileFormValues>) =>
      Boolean(data.agreeToReceiveEmails) !== Boolean(userData?.agree_to_receive_emails),
    [userData],
  );

  const buildProfilePatch = useCallback(
    (data: ProfileFormValues): PsychologistProfilePatch => {
      if (!profile) return {};
      const patch: PsychologistProfilePatch = {};
      if ((data.firstName ?? '') !== (profile.first_name ?? '')) patch.first_name = data.firstName;
      if ((data.lastName ?? '') !== (profile.last_name ?? '')) patch.last_name = data.lastName;
      if ((data.middleName ?? '') !== (profile.middle_name ?? '')) patch.middle_name = data.middleName ?? '';
      if ((data.phone ?? '') !== (profile.phone_number ?? '')) patch.phone_number = data.phone;
      if ((data.specialization ?? '') !== (profile.specialization ?? '')) {
        patch.specialization = data.specialization || null;
      }
      return patch;
    },
    [profile],
  );

  const hasFormChanges = useCallback(
    (data: Partial<ProfileFormValues>) => {
      if (!profile) return false;
      return Object.keys(buildProfilePatch(data as ProfileFormValues)).length > 0 || isNewsletterChanged(data);
    },
    [profile, buildProfilePatch, isNewsletterChanged],
  );

  const onSubmit = async (data: ProfileFormValues) => {
    if (!hasFormChanges(data)) return;
    const patch = buildProfilePatch(data);

    try {
      // Пустой payload PATCH отвергает с 400 — отправляем только при реальных изменениях.
      if (Object.keys(patch).length > 0) {
        await updateProfile(patch).unwrap();
      }
      if (userData && isNewsletterChanged(data)) {
        const updatedUser = await changeUser({
          id: userData.id,
          agree_to_receive_emails: data.agreeToReceiveEmails,
        }).unwrap();
        dispatch(setUserData(updatedUser));
      }
      toast.success('Данные успешно сохранены');
    } catch (error: unknown) {
      // Повтор того же PATCH идемпотентен, поэтому при частичном сбое просим сохранить ещё раз.
      toast.error(
        getApiErrorCode(error) === 'PROFILE_UPDATE_PARTIAL_FAILURE'
          ? 'Данные сохранены не полностью, попробуйте сохранить ещё раз'
          : 'Не удалось сохранить данные, попробуйте позже',
      );
    }
  };

  const categories = useMemo(
    () => (specializationResponse?.data ?? []).slice().sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)),
    [specializationResponse],
  );

  const specializationTitles = useMemo(
    () => categories.map((item) => item.title).filter((title): title is string => Boolean(title)),
    [categories],
  );

  const specializationTitleById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((item) => {
      if (item.title) map.set(String(item.id), item.title);
    });
    return map;
  }, [categories]);

  const specializationIdByTitle = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((item) => {
      if (item.title) map.set(item.title, String(item.id));
    });
    return map;
  }, [categories]);

  const watchedValues = watch();
  const isSaveButtonEnabled = hasFormChanges(watchedValues as Partial<ProfileFormValues>);

  const hotOffer = useMemo(() => parseProfileHotOfferPayload(hotOfferPayload), [hotOfferPayload]);
  const isHotOfferDismissed = useMemo(() => {
    if (!hotOffer) return true;
    if (dismissedHotOfferId === hotOffer.id) return true;
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem(getProfileHotOfferCompletedStorageKey(hotOffer.id)) === '1') return true;
    const snoozedUntilRaw = localStorage.getItem(getProfileHotOfferSnoozedUntilStorageKey(hotOffer.id));
    if (!snoozedUntilRaw) return false;
    const snoozedUntil = Number(snoozedUntilRaw);
    if (!Number.isFinite(snoozedUntil)) {
      localStorage.removeItem(getProfileHotOfferSnoozedUntilStorageKey(hotOffer.id));
      return false;
    }
    if (Date.now() < snoozedUntil) return true;
    localStorage.removeItem(getProfileHotOfferSnoozedUntilStorageKey(hotOffer.id));
    return false;
  }, [dismissedHotOfferId, hotOffer]);

  const dismissHotOffer = () => {
    if (!hotOffer) return;
    const snoozedUntil = Date.now() + hotOffer.showAgainAfterHours * 60 * 60 * 1000;
    localStorage.setItem(getProfileHotOfferSnoozedUntilStorageKey(hotOffer.id), String(snoozedUntil));
    setDismissedHotOfferId(hotOffer.id);
  };

  const acceptHotOffer = () => {
    if (!hotOffer) return;
    if (hotOffer.primaryLink) {
      if (hotOffer.primaryLink.startsWith('/')) {
        navigate(hotOffer.primaryLink);
      } else {
        try {
          const parsedUrl = new URL(hotOffer.primaryLink);
          const isInternal = parsedUrl.origin === window.location.origin;
          if (isInternal) {
            navigate(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`);
          } else {
            window.open(hotOffer.primaryLink, '_blank', 'noopener,noreferrer');
          }
        } catch {
          window.open(hotOffer.primaryLink, '_blank', 'noopener,noreferrer');
        }
      }
    } else if (hotOffer.successToast?.trim()) {
      toast.success(hotOffer.successToast.trim());
    }
    localStorage.setItem(getProfileHotOfferCompletedStorageKey(hotOffer.id), '1');
    setDismissedHotOfferId(hotOffer.id);
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'general', label: 'Общая информация' },
    ...(isCatalogProfileEnabled ? [{ id: 'catalog' as const, label: 'Профиль в каталоге' }] : []),
  ];

  useEffect(() => {
    if (!isCatalogProfileEnabled && activeTab === 'catalog') {
      setActiveTab('general');
    }
  }, [activeTab, isCatalogProfileEnabled]);

  const handleTabClick = (tab: Tab) => {
    if (tab === activeTab) return;
    if (activeTab === 'catalog' && isCatalogTabDirty) {
      setPendingTab(tab);
      return;
    }
    setActiveTab(tab);
  };

  return (
    <div className="w-full h-full pb-[36px]">
      <TabList variant="segmented" className="mb-6" items={tabs} value={activeTab} onChange={handleTabClick} />

      {activeTab === 'general' && (
        <div className="w-full flex flex-col xl:flex-row gap-[40px] lg:gap-[24px]">
          <div className="flex w-full max-w-[330px] flex-col items-center gap-[30px] shrink-0">
            {hotOffer && !isHotOfferDismissed && (
              <HotOfferCard
                className="w-full shrink-0"
                title={hotOffer.title}
                description={hotOffer.description}
                primaryLabel={hotOffer.primaryLabel}
                secondaryLabel={hotOffer.secondaryLabel}
                onAccept={acceptHotOffer}
                onDismiss={dismissHotOffer}
              />
            )}
            <div className="w-full h-[350px] lg:h-[320px] flex items-center justify-center rounded-card overflow-hidden relative">
              {isLoadingAva ? (
                <Loader embedInParent />
              ) : (
                <img src={profile?.avatar_url || photo} className="w-full h-full object-cover" alt="" />
              )}
              {avatarPicker.input}
              <Pressable
                onClick={avatarPicker.open}
                className="absolute inset-0 flex items-center justify-center bg-black/0 text-heading-md text-white underline opacity-0 transition-opacity hover:bg-black/40 hover:opacity-100 focus-visible:bg-black/40 focus-visible:opacity-100"
              >
                Изменить
              </Pressable>
            </div>
            <Button size="md" variant="outline" fullWidth endIcon={<ArrowTopRight />} onClick={showCodex}>
              Этический кодекс психолога
            </Button>
            <LogoutButton />
          </div>

          <form className="flex min-w-0 flex-1 flex-col" onSubmit={handleSubmit(onSubmit)}>
            <Card variant="raised" padding="lg" className="w-full">
              <Heading size="lg" className="mb-6">
                Общая информация
              </Heading>
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        classNameWrap="md:min-w-0"
                        size="lg"
                        label="Фамилия *"
                        showRequired
                        placeholder="Введите фамилию"
                        onChange={field.onChange}
                        value={field.value || ''}
                        error={error?.message}
                        maxLength={20}
                      />
                    )}
                  />
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        classNameWrap="md:min-w-0"
                        size="lg"
                        label="Имя *"
                        showRequired
                        placeholder="Введите имя"
                        onChange={field.onChange}
                        value={field.value || ''}
                        error={error?.message}
                        maxLength={16}
                      />
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Controller
                    name="middleName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        classNameWrap="md:min-w-0"
                        size="lg"
                        label="Отчество"
                        placeholder="Введите отчество"
                        onChange={field.onChange}
                        value={field.value || ''}
                        error={error?.message}
                        maxLength={20}
                      />
                    )}
                  />
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <PhoneInputWithMask
                        classNameWrap="md:min-w-0"
                        size="lg"
                        label="Телефон *"
                        showRequired
                        selected={field.value}
                        onChange={field.onChange}
                        onBlur={() => field.onBlur()}
                        error={error?.message}
                      />
                    )}
                  />
                </div>

                <Controller
                  name="specialization"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="w-full">
                      <CustomSelect
                        size="lg"
                        label="Специализация"
                        items={specializationTitles}
                        placeholder="Выберите категорию"
                        selected={specializationTitleById.get(String(field.value)) || ''}
                        onChange={(val) => field.onChange(specializationIdByTitle.get(val) ?? '')}
                        error={error?.message}
                        showError={true}
                        isChange={false}
                      />
                    </div>
                  )}
                />

                <Input
                  size="lg"
                  className="cursor-default"
                  classNameWrap="w-full"
                  label="Email"
                  placeholder="Введите email"
                  type="email"
                  autoComplete="email"
                  readOnly
                  value={profile?.email || ''}
                  icon={{
                    type: 'suffix',
                    icon: (
                      <Tooltip text="Для смены email надо обратиться к администратору сервиса info@zingizi.pro">
                        <span className="pointer-events-auto inline-flex items-center text-ink-subtle hover:text-ink-muted">
                          <UserCircle className="h-5 w-5" />
                        </span>
                      </Tooltip>
                    ),
                  }}
                />

                <Controller
                  name="agreeToReceiveEmails"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      size="sm"
                      label="Я согласен получать новости и рекламные рассылки"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />

                <Button type="submit" size="lg" variant="primary" fullWidth disabled={!isSaveButtonEnabled}>
                  Сохранить
                </Button>
              </div>
            </Card>
          </form>
        </div>
      )}

      {isCatalogProfileEnabled && activeTab === 'catalog' && <CatalogProfileTab onDirtyChange={setIsCatalogTabDirty} />}

      {(isShowCodex || !userData?.agree_with_code) && <ConfirmCodex closeCodex={() => setIsShowCodex(false)} />}

      <UnsavedChangesModal
        isOpen={pendingTab !== null}
        onStay={() => setPendingTab(null)}
        onLeave={() => {
          if (pendingTab) setActiveTab(pendingTab);
          setIsCatalogTabDirty(false);
          setPendingTab(null);
        }}
      />
    </div>
  );
};

export default Profile;
